import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

/**
 * Cancels a previously submitted CDM / OCF / PP review (HL7 OBR.25='X').
 *
 * BE contract (KIN-1033): three PUT endpoints under
 *   api/practice/v1/{tenant}/cdm/patients/{patientId}/...
 *     CDM -> /cdm/cancel   (mirrors the /cdm/submit base)
 *     OCF -> /ocf/cancel
 *     PP  -> /pp/cancel
 *   body: { originalReimbursementMessageControlId: "<control id>" }
 *
 * These endpoints use the CDM legacy handler: on failure they return HTTP 500
 * with { success: false, message } rather than the standard ApiResponse
 * envelope. We therefore branch on response.data.success / message and never
 * on HTTP status — a 500 may still carry a usable message.
 */
const PROGRAMME_PATH_SEGMENT = {
  CDM: "cdm",
  OCF: "ocf",
  PP: "pp",
};

export const useCancelReview = ({ patientId, programme }) => {
  const queryClient = useQueryClient();

  const tenantVal = localStorage.getItem("kinesin-tenant");

  return useMutation({
    mutationFn: async ({ originalReimbursementMessageControlId }) => {
      const axiosInstance = createAxiosInstance();
      const segment = PROGRAMME_PATH_SEGMENT[programme];

      try {
        const response = await axiosInstance.put(
          `${tenantVal}/cdm/patients/${patientId}/${segment}/cancel`,
          { originalReimbursementMessageControlId }
        );

        return response.data;
      } catch (error) {
        const data = error?.response?.data;

        if (data && typeof data.success === "boolean") {
          return data;
        }

        throw error;
      }
    },
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data.message || "Review cancelled.");
        queryClient.invalidateQueries({ queryKey: ["patientCDMStatus"] });
        queryClient.invalidateQueries({ queryKey: ["patient-cdm"] });
        queryClient.invalidateQueries({ queryKey: ["patientCDM"] });
        queryClient.invalidateQueries({ queryKey: ["reviewDetails"] });

        return;
      }

      toast.error(data?.message || "Could not cancel the review.");
    },
    onError: (error) => {
      console.error(error);

      toast.error(
        error?.response?.data?.message ||
          "Something went wrong while cancelling the review."
      );
    },
  });
};
